import { useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { 
  Edit3, 
  Save, 
  X, 
  Link, 
  CheckCircle, 
  AlertTriangle,
  Package,
  DollarSign,
  Hash
} from "lucide-react";
import { InvoiceItem, Product } from "@/hooks/useWorkstationStore";
import { cn } from "@/lib/utils";

interface InvoiceItemRowProps {
  invoiceItem: InvoiceItem;
  matchingProducts?: Product[];
  onEdit?: (itemId: string, updates: Partial<InvoiceItem>) => void;
  onMatch?: (itemId: string, productId: string) => void;
  onShowMatchDrawer?: (itemId: string) => void;
  variant?: 'compact' | 'detailed';
}

export const InvoiceItemRow = ({ 
  invoiceItem, 
  matchingProducts = [],
  onEdit,
  onMatch,
  onShowMatchDrawer,
  variant = 'detailed' 
}: InvoiceItemRowProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValues, setEditValues] = useState({
    master_case_quantity: invoiceItem.master_case_quantity?.toString() || '',
    units_per_case: invoiceItem.units_per_case?.toString() || '',
    units_per_box: invoiceItem.units_per_box?.toString() || ''
  });

  const handleSave = () => {
    const updates: Partial<InvoiceItem> = {};
    
    if (editValues.master_case_quantity !== '') {
      updates.master_case_quantity = parseFloat(editValues.master_case_quantity) || undefined;
    }
    if (editValues.units_per_case !== '') {
      updates.units_per_case = parseFloat(editValues.units_per_case) || undefined;
    }
    if (editValues.units_per_box !== '') {
      updates.units_per_box = parseFloat(editValues.units_per_box) || undefined;
    }

    if (onEdit && Object.keys(updates).length > 0) {
      onEdit(invoiceItem.id, updates);
    }
    
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValues({
      master_case_quantity: invoiceItem.master_case_quantity?.toString() || '',
      units_per_case: invoiceItem.units_per_case?.toString() || '',
      units_per_box: invoiceItem.units_per_box?.toString() || ''
    });
    setIsEditing(false);
  };

  const getMatchStatus = () => {
    if (invoiceItem.matched_product_id) {
      return {
        status: 'matched',
        icon: CheckCircle,
        label: 'Matched',
        color: 'bg-green-100 text-green-700 border-green-200'
      };
    } else if (matchingProducts.length > 0) {
      return {
        status: 'candidates',
        icon: AlertTriangle,
        label: 'Has Candidates',
        color: 'bg-yellow-100 text-yellow-700 border-yellow-200'
      };
    } else {
      return {
        status: 'unmatched',
        icon: X,
        label: 'Unmatched',
        color: 'bg-gray-100 text-gray-700 border-gray-200'
      };
    }
  };

  const matchStatus = getMatchStatus();
  const StatusIcon = matchStatus.icon;

  // Find exact barcode matches
  const exactMatches = matchingProducts.filter(p => p.barcode === invoiceItem.barcode_upc);
  const fuzzyMatches = matchingProducts.filter(p => p.barcode !== invoiceItem.barcode_upc);

  if (variant === 'compact') {
    return (
      <Card className={cn("transition-all hover:shadow-sm", matchStatus.color)}>
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{invoiceItem.product_name}</div>
              <div className="text-xs text-gray-500">
                {invoiceItem.barcode_upc && `${invoiceItem.barcode_upc} • `}
                {invoiceItem.total_cost && `$${invoiceItem.total_cost.toFixed(2)}`}
                {invoiceItem.quantity_boxes && ` • ${invoiceItem.quantity_boxes} boxes`}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className={cn("text-xs", matchStatus.color)}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {matchStatus.label}
              </Badge>
              
              {invoiceItem.match_confidence && (
                <span className="text-xs font-medium">
                  {invoiceItem.match_confidence}%
                </span>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={cn("transition-all hover:shadow-md", matchStatus.color)}>
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={cn("text-xs", matchStatus.color)}>
              <StatusIcon className="w-3 h-3 mr-1" />
              {matchStatus.label}
            </Badge>
            
            {invoiceItem.match_confidence && (
              <Badge variant="secondary" className="text-xs">
                {invoiceItem.match_confidence}% confidence
              </Badge>
            )}
            
            <Badge 
              variant={invoiceItem.validation_status === 'approved' ? 'default' : 'secondary'}
              className="text-xs"
            >
              {invoiceItem.validation_status}
            </Badge>
          </div>
          
          <div className="flex gap-1">
            {!isEditing && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="h-8 w-8 p-0"
              >
                <Edit3 className="w-3 h-3" />
              </Button>
            )}
            
            {onShowMatchDrawer && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onShowMatchDrawer(invoiceItem.id)}
                className="h-8 w-8 p-0"
              >
                <Link className="w-3 h-3" />
              </Button>
            )}
          </div>
        </div>

        {/* Product Information */}
        <div className="mb-3">
          <h3 className="font-medium text-gray-900 mb-1">{invoiceItem.product_name}</h3>
          <div className="flex items-center gap-4 text-sm text-gray-600">
            {invoiceItem.brand && (
              <span>Brand: {invoiceItem.brand}</span>
            )}
            {invoiceItem.barcode_upc && (
              <span>Barcode: {invoiceItem.barcode_upc}</span>
            )}
            {invoiceItem.pack_info && (
              <span>Pack: {invoiceItem.pack_info}</span>
            )}
          </div>
        </div>

        {/* Pricing Information */}
        <div className="grid grid-cols-3 gap-3 mb-3">
          <div className="flex items-center gap-2">
            <Hash className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-sm font-medium">
                {invoiceItem.quantity || 'N/A'}
              </div>
              <div className="text-xs text-gray-500">Quantity</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-sm font-medium">
                {invoiceItem.unit_price ? `$${invoiceItem.unit_price.toFixed(2)}` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500">Unit Price</div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-gray-400" />
            <div>
              <div className="text-sm font-medium">
                {invoiceItem.total_cost ? `$${invoiceItem.total_cost.toFixed(2)}` : 'N/A'}
              </div>
              <div className="text-xs text-gray-500">Total Cost</div>
            </div>
          </div>
        </div>

        {/* ChatGPT Extracted Data */}
        {(invoiceItem.master_case_quantity || invoiceItem.units_per_case || invoiceItem.units_per_box || isEditing) && (
          <div className="bg-blue-50 rounded-lg p-3 mb-3">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-blue-900">AI Extracted Data:</span>
              {!isEditing ? (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="h-6 px-2 text-xs"
                >
                  <Edit3 className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              ) : (
                <div className="flex gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleSave}
                    className="h-6 px-2 text-xs text-green-600"
                  >
                    <Save className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    className="h-6 px-2 text-xs text-red-600"
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>

            {isEditing ? (
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Master Cases:</label>
                  <Input
                    type="number"
                    value={editValues.master_case_quantity}
                    onChange={(e) => setEditValues(prev => ({ ...prev, master_case_quantity: e.target.value }))}
                    className="h-8 text-xs"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Units/Case:</label>
                  <Input
                    type="number"
                    value={editValues.units_per_case}
                    onChange={(e) => setEditValues(prev => ({ ...prev, units_per_case: e.target.value }))}
                    className="h-8 text-xs"
                    placeholder="0"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Units/Box:</label>
                  <Input
                    type="number"
                    value={editValues.units_per_box}
                    onChange={(e) => setEditValues(prev => ({ ...prev, units_per_box: e.target.value }))}
                    className="h-8 text-xs"
                    placeholder="0"
                  />
                </div>
              </div>
            ) : (
              <div className="text-xs text-blue-700 space-y-1">
                {invoiceItem.master_case_quantity && (
                  <div>Master Cases: {invoiceItem.master_case_quantity}</div>
                )}
                {invoiceItem.units_per_case && (
                  <div>Units per Case: {invoiceItem.units_per_case}</div>
                )}
                {invoiceItem.units_per_box && (
                  <div>Units per Box: {invoiceItem.units_per_box}</div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Matching Candidates */}
        {!invoiceItem.matched_product_id && (exactMatches.length > 0 || fuzzyMatches.length > 0) && (
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">Matching Candidates:</div>
            
            {/* Exact Matches */}
            {exactMatches.map((product) => (
              <Button
                key={product.id}
                variant="default"
                size="sm"
                className="w-full justify-start bg-green-50 text-green-700 border-green-200 hover:bg-green-100"
                onClick={() => onMatch?.(invoiceItem.id, product.id)}
              >
                🎯 {product.name} ({product.barcode})
                {invoiceItem.total_cost && ` - $${invoiceItem.total_cost.toFixed(2)}`}
              </Button>
            ))}
            
            {/* Fuzzy Matches */}
            {fuzzyMatches.slice(0, 3).map((product) => (
              <Button
                key={product.id}
                variant="outline"
                size="sm"
                className="w-full justify-start"
                onClick={() => onMatch?.(invoiceItem.id, product.id)}
              >
                {product.name} ({product.barcode})
                {invoiceItem.total_cost && ` - $${invoiceItem.total_cost.toFixed(2)}`}
              </Button>
            ))}
            
            {fuzzyMatches.length > 3 && (
              <Button
                variant="ghost"
                size="sm"
                className="w-full"
                onClick={() => onShowMatchDrawer?.(invoiceItem.id)}
              >
                +{fuzzyMatches.length - 3} more matches
              </Button>
            )}
          </div>
        )}

        {/* Matched Product Info */}
        {invoiceItem.matched_product_id && (
          <div className="bg-green-50 rounded-lg p-2">
            <div className="flex items-center gap-2 text-sm text-green-700">
              <CheckCircle className="w-4 h-4" />
              <span>Matched to product {invoiceItem.matched_product_id}</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};